import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, ClipboardText, MagnifyingGlass, X, CheckCircle, XCircle, PaperPlaneRight,
  PencilSimple, Trash, Clock, UploadSimple, DownloadSimple, FileText,
  Sparkle, MagicWand, ArrowClockwise, Check, Scales,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  rascunho: { label: "Rascunho", bg: "bg-[#334155]/40", text: "text-[#94A3B8]", border: "border-[#334155]" },
  em_analise: { label: "Em Análise", bg: "bg-[#FFDF00]/15", text: "text-[#FFDF00]", border: "border-[#FFDF00]/40" },
  aprovada: { label: "Aprovada", bg: "bg-[#009C3B]/15", text: "text-[#12B850]", border: "border-[#009C3B]/40" },
  rejeitada: { label: "Rejeitada", bg: "bg-[#EF4444]/15", text: "text-[#F87171]", border: "border-[#EF4444]/40" },
};

const CATEGORIAS = { bens: "Bens", servicos: "Serviços", obras: "Obras", tic: "TIC (Tecnologia)" };

function fmtMoeda(v) {
  return `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtData(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

export default function Demandas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [municipioId, setMunicipioId] = useState("");
  const [municipios, setMunicipios] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    if (isAdmin && municipioId) p.set("municipio_id", municipioId);
    api.get(`/demandas?${p.toString()}`)
      .then((r) => setItems(r.data?.items || []))
      .finally(() => setLoading(false));
    api.get(`/demandas/stats${isAdmin && municipioId ? `?municipio_id=${municipioId}` : ""}`)
      .then((r) => setStats(r.data || {}));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, municipioId]);
  useEffect(() => {
    if (isAdmin) api.get("/municipalities").then((r) => setMunicipios(r.data || []));
  }, [isAdmin]);

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="uppercase-label flex items-center gap-1.5">
            <ClipboardText size={12} weight="duotone" className="text-[#FFDF00]" /> Gestão de contratações
          </div>
          <h1 className="font-heading text-4xl mt-1">Demandas</h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            Formalização e aprovação das solicitações de contratação das secretarias (DFD).
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="demandas-new"
                className="px-4 py-2.5 rounded-md bg-[#FFDF00] text-[#002776] font-semibold flex items-center gap-2 shadow-sm">
          <Plus size={14} weight="bold" /> Nova Demanda
        </button>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card label="Total" qtd={stats.total?.qtd || 0} valor={stats.total?.valor || 0} tone="muted" />
        <Card label="Rascunho" qtd={stats.rascunho?.qtd} valor={stats.rascunho?.valor} tone="rascunho" />
        <Card label="Em Análise" qtd={stats.em_analise?.qtd} valor={stats.em_analise?.valor} tone="em_analise" />
        <Card label="Aprovadas" qtd={stats.aprovada?.qtd} valor={stats.aprovada?.valor} tone="aprovada" />
        <Card label="Rejeitadas" qtd={stats.rejeitada?.qtd} valor={stats.rejeitada?.valor} tone="rejeitada" />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Buscar por número, objeto ou secretaria…"
                  className="tce-input w-full pl-9 pr-3 py-2 rounded-md text-sm"
                  data-testid="demandas-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="tce-input px-3 py-2 rounded-md text-sm" data-testid="demandas-status">
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="em_analise">Em Análise</option>
          <option value="aprovada">Aprovadas</option>
          <option value="rejeitada">Rejeitadas</option>
        </select>
        {isAdmin && (
          <select value={municipioId} onChange={(e) => setMunicipioId(e.target.value)}
                  className="tce-input px-3 py-2 rounded-md text-sm" data-testid="demandas-muni">
            <option value="">Todos os municípios</option>
            {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
          </select>
        )}
      </div>

      {/* Lista */}
      <div className="tce-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] border-b border-[#334155]">
            <tr className="text-left text-[#94A3B8]">
              <th className="px-4 py-3 font-medium">Nº</th>
              <th className="px-4 py-3 font-medium">Secretaria</th>
              <th className="px-4 py-3 font-medium">Objeto</th>
              <th className="px-4 py-3 font-medium text-right">Valor</th>
              <th className="px-4 py-3 font-medium">Prazo</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Criada em</th>
              <th className="px-4 py-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="px-4 py-10 text-center text-[#94A3B8]">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-10 text-center text-[#94A3B8]">Nenhuma demanda encontrada</td></tr>
            )}
            {items.map((d) => {
              const sm = STATUS_META[d.status] || STATUS_META.rascunho;
              return (
                <tr key={d.id} className="border-b border-[#334155]/60 hover:bg-[#334155]/20"
                    data-testid={`dem-row-${d.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.numero_gerado}</td>
                  <td className="px-4 py-3">{d.secretaria_solicitante}</td>
                  <td className="px-4 py-3 max-w-[320px] truncate" title={d.objeto}>{d.objeto}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoeda(d.valor_estimado)}</td>
                  <td className="px-4 py-3 text-xs">{fmtData(d.prazo_desejado)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                      {sm.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{fmtData(d.created_at)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setEditing(d); setShowForm(true); }}
                            className="px-2 py-1 rounded border border-[#334155] hover:border-[#FFDF00] text-xs"
                            data-testid={`dem-open-${d.id}`}>Abrir</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <DemandaFormModal
          dem={editing}
          isAdmin={isAdmin}
          municipios={municipios}
          user={user}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Card({ label, qtd, valor, tone }) {
  const palette = {
    muted: "border-[#334155] text-[#E9EEF5]",
    rascunho: "border-[#334155] text-[#94A3B8]",
    em_analise: "border-[#FFDF00]/40 text-[#FFDF00]",
    aprovada: "border-[#009C3B]/40 text-[#12B850]",
    rejeitada: "border-[#EF4444]/40 text-[#F87171]",
  };
  return (
    <div className={`tce-card p-4 border ${palette[tone]}`}>
      <div className="uppercase-label text-[10px]">{label}</div>
      <div className="font-heading text-2xl mt-1">{qtd || 0}</div>
      <div className="text-xs text-[#94A3B8] mt-1 font-mono">{fmtMoeda(valor)}</div>
    </div>
  );
}


function DemandaFormModal({ dem, isAdmin, municipios, user, onClose, onSaved }) {
  const isEdit = !!dem;
  const [form, setForm] = useState({
    municipio_id: dem?.municipio_id || user?.municipio_id || "",
    secretaria_solicitante: dem?.secretaria_solicitante || "",
    objeto: dem?.objeto || "",
    valor_estimado: dem?.valor_estimado || 0,
    justificativa: dem?.justificativa || "",
    prazo_desejado: dem?.prazo_desejado || "",
    categoria: dem?.categoria || "",
  });
  const [parecer, setParecer] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(dem);
  const [sugCampo, setSugCampo] = useState("justificativa");
  const [sugInstrucao, setSugInstrucao] = useState("");
  const [sugResult, setSugResult] = useState(null);
  const [sugBusy, setSugBusy] = useState(false);
  const [showIA, setShowIA] = useState(false);
  const sm = current ? (STATUS_META[current.status] || STATUS_META.rascunho) : null;
  const canApprove = user?.role === "admin" || user?.role === "gestor";
  const editable = !current || current.status === "rascunho" || current.status === "em_analise";

  const pedirSugestao = async (tipo) => {
    if (!current) { toast.error("Salve a demanda primeiro"); return; }
    setSugBusy(true); setSugResult(null);
    try {
      const r = await api.post(`/demandas/${current.id}/sugestao-ia`, {
        tipo, campo: sugCampo, instrucao: sugInstrucao || null,
      });
      setSugResult({ ...r.data, tipo });
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha na IA"); }
    finally { setSugBusy(false); }
  };

  const aplicarSugestao = () => {
    if (!sugResult?.sugestao_html) return;
    // strip HTML tags to plain text para campos de input/textarea
    const plain = sugResult.sugestao_html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    setForm((f) => ({ ...f, [sugResult.campo || "justificativa"]: plain }));
    setSugResult(null);
    toast.success(`${sugResult.campo} atualizado — não esqueça de salvar`);
  };

  const aplicarModelo = async () => {
    if (!current) { toast.error("Salve a demanda primeiro"); return; }
    if (!window.confirm("Aplicar o modelo padrão de DFD (Lei 14.133 + TCU) à justificativa?")) return;
    setBusy(true);
    try {
      await api.post(`/demandas/${current.id}/aplicar-modelo`);
      // Buscar demanda atualizada
      const r = await api.get(`/demandas/${current.id}`);
      setCurrent(r.data);
      toast.success("Modelo padrão disponível na seção de histórico — copie e refine conforme necessário");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const salvar = async () => {
    setBusy(true);
    try {
      if (!isEdit) {
        const r = await api.post("/demandas", {
          ...form,
          valor_estimado: Number(form.valor_estimado) || 0,
          prazo_desejado: form.prazo_desejado || null,
          categoria: form.categoria || null,
        });
        toast.success(`Demanda ${r.data.numero_gerado} criada`);
        setCurrent(r.data);
      } else {
        const r = await api.patch(`/demandas/${current.id}`, {
          ...form, valor_estimado: Number(form.valor_estimado) || 0,
          prazo_desejado: form.prazo_desejado || null, categoria: form.categoria || null,
        });
        toast.success("Demanda atualizada");
        setCurrent(r.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setBusy(false); }
  };

  const transicao = async (acao, path) => {
    if (!current) { toast.error("Salve primeiro"); return; }
    if (acao === "rejeitar" && !parecer) { toast.error("Informe o parecer"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/demandas/${current.id}/${path}`, { parecer: parecer || null });
      toast.success(`Demanda ${r.data.status.replace("_", " ")}`);
      setCurrent(r.data);
      setParecer("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const excluir = async () => {
    if (!current) { onClose(); return; }
    if (!window.confirm("Excluir esta demanda definitivamente?")) return;
    try {
      await api.delete(`/demandas/${current.id}`);
      toast.success("Demanda excluída");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const uploadAnexo = async (file) => {
    if (!current) { toast.error("Salve primeiro para anexar"); return; }
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Arquivo maior que 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/demandas/${current.id}/anexos`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.get(`/demandas/${current.id}`);
      setCurrent(r.data);
      toast.success("Anexo enviado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro no upload"); }
    finally { setBusy(false); }
  };

  const removerAnexo = async (idx) => {
    if (!window.confirm("Remover anexo?")) return;
    try {
      await api.delete(`/demandas/${current.id}/anexos/${idx}`);
      const r = await api.get(`/demandas/${current.id}`);
      setCurrent(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[92vh] bg-[#1E293B] border border-[#334155] rounded-lg flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#334155] flex items-start justify-between">
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <ClipboardText size={12} weight="duotone" className="text-[#FFDF00]" />
              {isEdit ? `Demanda ${current?.numero_gerado}` : "Nova demanda"}
            </div>
            <h2 className="font-heading text-2xl mt-1">
              {isEdit ? current?.objeto?.slice(0, 60) : "Formalização da demanda"}
            </h2>
            {sm && (
              <span className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>
                {sm.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#334155] rounded" data-testid="dem-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isAdmin && (
              <div className="md:col-span-2">
                <label className="uppercase-label block mb-1">Município *</label>
                <select value={form.municipio_id} onChange={(e) => setForm({ ...form, municipio_id: e.target.value })}
                        disabled={isEdit || !editable}
                        className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="dem-field-muni">
                  <option value="">Selecione…</option>
                  {municipios.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="uppercase-label block mb-1">Secretaria solicitante *</label>
              <input value={form.secretaria_solicitante} onChange={(e) => setForm({ ...form, secretaria_solicitante: e.target.value })}
                      disabled={!editable}
                      placeholder='Ex.: "Secretaria de Saúde"'
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="dem-field-secretaria" />
            </div>
            <div>
              <label className="uppercase-label block mb-1">Categoria</label>
              <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                      disabled={!editable}
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="dem-field-categoria">
                <option value="">—</option>
                {Object.entries(CATEGORIAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="uppercase-label block mb-1">Objeto da solicitação *</label>
              <input value={form.objeto} onChange={(e) => setForm({ ...form, objeto: e.target.value })}
                      disabled={!editable}
                      placeholder='Ex.: "Aquisição de medicamentos para a rede básica"'
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="dem-field-objeto" />
            </div>
            <div>
              <label className="uppercase-label block mb-1">Valor estimado (R$)</label>
              <input type="number" value={form.valor_estimado} onChange={(e) => setForm({ ...form, valor_estimado: e.target.value })}
                      disabled={!editable}
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="dem-field-valor" />
            </div>
            <div>
              <label className="uppercase-label block mb-1">Prazo desejado</label>
              <input type="date" value={form.prazo_desejado || ""} onChange={(e) => setForm({ ...form, prazo_desejado: e.target.value })}
                      disabled={!editable}
                      className="tce-input w-full px-3 py-2 rounded-md text-sm" data-testid="dem-field-prazo" />
            </div>
            <div className="md:col-span-2">
              <label className="uppercase-label block mb-1">Justificativa *</label>
              <textarea value={form.justificativa} onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
                        disabled={!editable} rows={4}
                        placeholder="Fundamentação técnica, demanda operacional e impacto esperado…"
                        className="tce-input w-full px-3 py-2 rounded-md text-sm resize-none"
                        data-testid="dem-field-justificativa" />
            </div>
          </div>

          {editable && current && (
            <div className="bg-[#0F172A] border border-[#FFDF00]/30 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="uppercase-label flex items-center gap-1">
                  <Sparkle size={11} className="text-[#FFDF00]" /> Assistente IA — DFD (Lei 14.133 + TCU)
                </label>
                <button onClick={() => setShowIA((s) => !s)}
                        className={`px-2 py-1 rounded text-[10px] font-semibold border transition ${
                          showIA ? "bg-[#FFDF00] text-[#002776] border-[#FFDF00]"
                                 : "bg-transparent text-[#FFDF00] border-[#FFDF00]/40 hover:bg-[#FFDF00]/10"}`}
                        data-testid="dem-toggle-ia">
                  {showIA ? "Fechar" : "Abrir"}
                </button>
              </div>
              {showIA && (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <select value={sugCampo} onChange={(e) => setSugCampo(e.target.value)}
                            className="tce-input px-2 py-1.5 rounded text-xs" data-testid="dem-ia-campo">
                      <option value="justificativa">Campo: Justificativa</option>
                      <option value="objeto">Campo: Objeto</option>
                    </select>
                    <input value={sugInstrucao} onChange={(e) => setSugInstrucao(e.target.value)}
                           placeholder="Instrução adicional (opcional)"
                           className="tce-input px-2 py-1.5 rounded text-xs" data-testid="dem-ia-instrucao" />
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 mb-2">
                    {[
                      { t: "melhorar", l: "Melhorar" },
                      { t: "expandir", l: "Expandir" },
                      { t: "continuar", l: "Continuar" },
                      { t: "conformidade", l: "Conformidade" },
                      { t: "resumir", l: "Resumir" },
                      { t: "corrigir", l: "Revisar" },
                    ].map((a) => (
                      <button key={a.t} onClick={() => pedirSugestao(a.t)} disabled={sugBusy}
                              className="px-2 py-1.5 rounded border border-[#334155] hover:border-[#FFDF00] text-[11px] text-[#CBD5E1] disabled:opacity-40"
                              data-testid={`dem-ia-${a.t}`}>
                        {a.l}
                      </button>
                    ))}
                  </div>
                  <button onClick={aplicarModelo} disabled={busy}
                          className="w-full mb-2 px-3 py-1.5 rounded border border-[#009C3B]/40 text-[#12B850] text-[11px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#009C3B]/10"
                          data-testid="dem-aplicar-modelo">
                    <Scales size={11} weight="duotone" /> Gerar modelo padrão de DFD (Lei 14.133 + TCU)
                  </button>
                  {sugBusy && (
                    <div className="bg-[#1E293B] border border-[#FFDF00]/40 rounded p-2 text-center">
                      <div className="animate-spin inline-block w-4 h-4 border-2 border-[#FFDF00] border-t-transparent rounded-full" />
                      <span className="text-xs text-[#94A3B8] ml-2">Consultando IA…</span>
                    </div>
                  )}
                  {sugResult?.sugestao_html && !sugBusy && (
                    <div className="bg-[#1E293B] border border-[#009C3B]/40 rounded">
                      <div className="px-2 py-1 bg-[#009C3B]/10 border-b border-[#009C3B]/30 flex items-center justify-between">
                        <span className="text-[10px] text-[#12B850] font-semibold flex items-center gap-1">
                          <CheckCircle size={11} weight="duotone" /> Sugestão para "{sugResult.campo}" ({sugResult.tipo})
                        </span>
                        <button onClick={() => pedirSugestao(sugResult.tipo)} className="p-1 hover:bg-[#334155] rounded">
                          <ArrowClockwise size={10} />
                        </button>
                      </div>
                      <div className="p-2 max-h-[160px] overflow-y-auto text-[11px] text-[#E9EEF5] leading-relaxed ai-response"
                           dangerouslySetInnerHTML={{ __html: sugResult.sugestao_html }}
                           data-testid="dem-ia-preview" />
                      <div className="p-1.5 border-t border-[#334155] flex gap-1">
                        <button onClick={aplicarSugestao}
                                className="flex-1 px-2 py-1 rounded bg-[#009C3B] text-white text-[10px] font-semibold flex items-center justify-center gap-1 hover:bg-[#12B850]"
                                data-testid="dem-ia-aplicar">
                          <Check size={10} weight="bold" /> Aplicar ao campo "{sugResult.campo}"
                        </button>
                        <button onClick={() => setSugResult(null)}
                                className="px-2 py-1 rounded border border-[#334155] text-[10px] text-[#94A3B8]">
                          Descartar
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {current && (
            <>
              {/* Anexos */}
              <div className="border-t border-[#334155] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="uppercase-label">Anexos ({current.anexos?.length || 0})</div>
                  {current.status !== "aprovada" && (
                    <label className="cursor-pointer px-3 py-1.5 rounded-md border border-[#FFDF00]/40 text-[#FFDF00] text-xs font-medium flex items-center gap-1 hover:bg-[#FFDF00]/10">
                      <UploadSimple size={12} weight="bold" /> Anexar arquivo
                      <input type="file" className="hidden"
                              onChange={(e) => uploadAnexo(e.target.files?.[0])}
                              data-testid="dem-upload" />
                    </label>
                  )}
                </div>
                {(current.anexos || []).map((a, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-[#0F172A] rounded border border-[#334155] px-3 py-2 mb-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText size={14} className="text-[#FFDF00]" />
                      <span>{a.filename}</span>
                      <span className="text-xs text-[#64748B]">{Math.round((a.size || 0) / 1024)}KB</span>
                    </div>
                    <div className="flex gap-1">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/demandas/${current.id}/anexos/${idx}`}
                          target="_blank" rel="noreferrer"
                          className="p-1.5 rounded hover:bg-[#334155] text-[#94A3B8]" title="Baixar">
                        <DownloadSimple size={14} />
                      </a>
                      {current.status !== "aprovada" && (
                        <button onClick={() => removerAnexo(idx)} className="p-1.5 rounded hover:bg-red-500/20 text-red-400"
                                title="Remover">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!current.anexos?.length && (
                  <div className="text-xs text-[#64748B] italic">Nenhum anexo enviado</div>
                )}
              </div>

              {/* Histórico */}
              <div className="border-t border-[#334155] pt-4">
                <div className="uppercase-label mb-2">Histórico de tramitação</div>
                <div className="space-y-1.5">
                  {(current.historico || []).map((h, i) => {
                    const smH = STATUS_META[h.status] || STATUS_META.rascunho;
                    return (
                      <div key={i} className="flex items-start gap-2 text-xs bg-[#0F172A] border border-[#334155] rounded px-3 py-2">
                        <Clock size={13} className="text-[#94A3B8] mt-0.5" />
                        <div className="flex-1">
                          <span className={`inline-block px-2 py-0.5 rounded border ${smH.bg} ${smH.text} ${smH.border} mr-2`}>{smH.label}</span>
                          <span className="text-[#94A3B8]">{h.obs}</span>
                          {h.parecer && <div className="text-[#E9EEF5] mt-1 italic">"{h.parecer}"</div>}
                          <div className="text-[#64748B] mt-0.5">por {h.por} · {new Date(h.em).toLocaleString("pt-BR")}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Parecer para ações */}
              {current.status !== "aprovada" && (
                <div className="border-t border-[#334155] pt-4">
                  <label className="uppercase-label block mb-1">Parecer / Observação</label>
                  <textarea value={parecer} onChange={(e) => setParecer(e.target.value)} rows={2}
                            placeholder="Parecer técnico ou motivo da rejeição…"
                            className="tce-input w-full px-3 py-2 rounded-md text-sm resize-none"
                            data-testid="dem-field-parecer" />
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-[#334155] flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {current && current.status !== "aprovada" && (
              <button onClick={excluir} className="px-3 py-2 rounded-md border border-red-500/40 text-red-400 text-xs flex items-center gap-1"
                      data-testid="dem-excluir">
                <Trash size={13} /> Excluir
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#334155] text-sm" data-testid="dem-cancel">
              Fechar
            </button>
            {editable && (
              <button onClick={salvar} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#002776] text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="dem-save">
                <PencilSimple size={13} weight="bold" /> {busy ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar demanda"}
              </button>
            )}
            {current?.status === "rascunho" && (
              <button onClick={() => transicao("submeter", "submeter")} disabled={busy}
                      className="px-4 py-2 rounded-md bg-[#FFDF00] text-[#002776] font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                      data-testid="dem-submeter">
                <PaperPlaneRight size={13} weight="bold" /> Submeter para análise
              </button>
            )}
            {current?.status === "em_analise" && canApprove && (
              <>
                <button onClick={() => transicao("rejeitar", "rejeitar")} disabled={busy}
                        className="px-4 py-2 rounded-md border border-red-500 text-red-400 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                        data-testid="dem-rejeitar">
                  <XCircle size={13} weight="bold" /> Rejeitar
                </button>
                <button onClick={() => transicao("aprovar", "aprovar")} disabled={busy}
                        className="px-4 py-2 rounded-md bg-[#009C3B] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                        data-testid="dem-aprovar">
                  <CheckCircle size={13} weight="bold" /> Aprovar demanda
                </button>
              </>
            )}
            {current?.status === "rejeitada" && (
              <button onClick={() => transicao("reabrir", "reabrir")} disabled={busy}
                      className="px-4 py-2 rounded-md border border-[#FFDF00]/40 text-[#FFDF00] text-sm font-medium flex items-center gap-2"
                      data-testid="dem-reabrir">
                Reabrir
              </button>
            )}
            {current?.status === "aprovada" && (
              <button onClick={async () => {
                if (!window.confirm("Promover esta demanda aprovada para um novo ETP?")) return;
                setBusy(true);
                try {
                  const r = await api.post(`/demandas/${current.id}/promover-etp`, {});
                  toast.success(`ETP ${r.data.numero_gerado} criado — acesse em "2. ETP"`);
                } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao promover"); }
                finally { setBusy(false); }
              }} disabled={busy}
                      className="px-4 py-2 rounded-md bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                      data-testid="dem-promover-etp">
                Promover para ETP
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
